import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import Icon from 'sharedComponents/core/Icon';
import CoreInput from 'sharedComponents/core/Input/CoreInput';
import { OnPurchase } from './PaymentForm/PaymentForm';
import environment, { FetchError } from 'api/environment';
import { commitMutation, graphql } from 'react-relay';
import { GraphError } from 'types/general';
import { Payment_PurchaseMutationResponse } from './__generated__/Payment_PurchaseMutation.graphql';

const PaymentForm = React.lazy(() =>
  import('sharedComponents/Payment/PaymentForm')
);

const useStyles = createUseStyles((theme: Theme) => ({
  paymentRoot: {},
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea2ad',
    margin: '0 0 10px'
  },
  orderList: {
    border: '1px solid #ededed',
    borderRadius: 10,
    overflow: 'hidden',
    marginBottom: 16
  },
  orderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gridGap: 12,
    padding: '11px 14px',
    fontSize: 13,
    borderBottom: '1px solid #ededed'
  },
  orderName: {
    fontWeight: 500,
    color: theme.colors.primaryBlack
  },
  orderPrice: {
    flexShrink: 0,
    color: '#74777f'
  },
  summaryRows: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: 4,
    padding: '10px 14px',
    background: '#f8f9fb'
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 13,
    color: '#74777f'
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 4,
    fontSize: 14,
    fontWeight: 700,
    color: theme.colors.primaryBlack
  },
  emailSection: {
    marginBottom: 16
  },
  emailLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: '#74777f',
    marginBottom: 7
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
    padding: '0 14px',
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 8,
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus-within': {
      borderColor: '#0b57ff',
      boxShadow: '0 0 0 3px rgba(11, 87, 255, 0.1)'
    },
    '& input': {
      width: '100%',
      padding: 0,
      border: 'none',
      outline: 'none',
      fontSize: 14,
      color: theme.colors.primaryBlack,
      '&::placeholder': {
        color: '#9ea2ad'
      }
    }
  },
  cardInfo: {
    marginBottom: 14
  },
  smallHeading: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea2ad',
    margin: '0 0 10px'
  },
  cardContent: {
    display: 'flex',
    justifyContent: 'space-between',
    gridGap: 16
  },
  cardRight: {
    flex: 2,
    textAlign: 'right'
  },
  cardRightImage: {
    display: 'inline-block'
  },
  cardLeft: {
    fontSize: 13,
    color: '#74777f',
    lineHeight: 1.5,
    flex: 3
  },
  stripeImage: {
    width: 164,
    marginBottom: 14,
    height: 45,
    '& img': {
      width: '100% !important',
      height: 'auto !important'
    }
  },
  paymentImage: {
    width: 196,
    height: 36,
    '& img': {
      width: '100% !important',
      height: 'auto !important'
    }
  }
}));

export type Course = {
  id: number;
  instanceUUID?: string;
  name: string;
  sku: string;
  price: number;
};

const mutation = graphql`
  mutation Payment_PurchaseMutation(
    $courses: [Int!]
    $classroomInstances: [UUID!]
    $users: [UUID!]
    $extraEmail: String
  ) {
    purchaseCourses(
      input: {
        onlineCourses: $courses
        classroomInstances: $classroomInstances
        users: $users
        extraInvoiceEmail: $extraEmail
        acceptedTerms: true
        backgroundCheckConfirm: true
      }
    ) {
      transactionComplete
      stripeClientSecret
    }
  }
`;

type PurchaseCallback = (
  response: Payment_PurchaseMutationResponse,
  error?: GraphError
) => void;

const PurchaseCourses = (
  courses: number[] | undefined,
  classroomInstances: string[] | undefined,
  users: string[] | undefined,
  extraEmail: string,
  callback?: PurchaseCallback
) => {
  const variables = {
    classroomInstances,
    courses,
    users,
    extraEmail
  };

  if (!users) {
    if (callback)
      callback(
        { purchaseCourses: null },
        {
          message: 'No users given, cannot purchase course without users',
          path: []
        }
      );
  }

  if (!courses && !classroomInstances) {
    if (callback)
      callback(
        { purchaseCourses: null },
        { message: 'Please select at least one course to purchase', path: [] }
      );
  }

  commitMutation(environment, {
    mutation,
    variables,
    onCompleted: async (
      response: Payment_PurchaseMutationResponse,
      errors: GraphError[]
    ) => {
      if (errors) {
        // Display error
        if (callback) {
          callback(response, errors[0]);
        }
        return;
      }

      if (callback) {
        callback(response, undefined);
      }
      console.log('Response received from server.', response, errors);
    },
    onError: (err: FetchError) => {
      if (callback)
        callback(
          { purchaseCourses: null },
          {
            message: err.message,
            path: [],
            extensions: {
              type: err?.type === false || err?.type === true ? '' : err?.type,
              message: err.message
            }
          }
        );
    }
  });
};

export default function Payment({
  courses,
  userUUIDs,
  isContract,
  onSuccess,
  onError,
  hideOrderCard,
  registerSubmit
}: {
  courses: Course[];
  userUUIDs?: string[];
  isContract: boolean;
  onSuccess: () => void;
  onError: (error: GraphError) => void;
  // When no card payment is needed, hide the "Place your order" card and let
  // the host trigger the purchase itself via the submit fn from registerSubmit
  hideOrderCard?: boolean;
  registerSubmit?: (submit: () => void) => void;
}) {
  const classes = useStyles();
  const coursePrice = courses
    .map(({ price }: { price: number }) => price)
    .reduce(
      (prevValue: number, currentValue: number) => prevValue + currentValue,
      0
    );
  const [email, setEmail] = React.useState('');

  const onPurchase = (callback?: PurchaseCallback) =>
    PurchaseCourses(
      courses
        .filter((course: Course) => !course.instanceUUID)
        .map((course: Course) => course.id),
      courses
        .filter((course: Course) => !!course.instanceUUID)
        .map((course: Course) => course.instanceUUID ?? ''),
      userUUIDs,
      email,
      callback
    );

  const quantity = userUUIDs ? userUUIDs.length || 1 : 1;
  const subTotal = coursePrice * quantity;
  const vat = Math.round(subTotal * (20 / 100) * 100) / 100;
  const totalDue = subTotal + vat;
  const requiresPayment = !isContract && totalDue > 0;

  return (
    <div className={classes.paymentRoot}>
      <div className={classes.sectionLabel}>Your Order</div>
      <div className={classes.orderList}>
        {courses.map(({ name, price }, index: string | number) => (
          <div className={classes.orderRow} key={index}>
            <span className={classes.orderName}>
              <strong>{quantity}x </strong>
              {name}
            </span>
            <span className={classes.orderPrice}>
              £{(price * quantity).toFixed(2)}
            </span>
          </div>
        ))}
        <div className={classes.summaryRows}>
          <div className={classes.summaryRow}>
            <span>Sub Total</span>
            <span>£{subTotal.toFixed(2)}</span>
          </div>
          <div className={classes.summaryRow}>
            <span>VAT (20%)</span>
            <span>£{vat.toFixed(2)}</span>
          </div>
          <div className={classes.totalRow}>
            <span>Total Due</span>
            <span>£{totalDue.toFixed(2)}</span>
          </div>
        </div>
      </div>
      <div className={classes.emailSection}>
        <div className={classes.emailLabel}>
          Please send a duplicate payment receipt to:
        </div>
        <div className={classes.inputWrapper}>
          <CoreInput
            type="text"
            onChange={(email) => {
              setEmail(email);
            }}
            placeholder={'email@email.com'}
          />
        </div>
      </div>
      <div id="PaymentForm">
        {requiresPayment && (
          <div className={classes.cardInfo}>
            <div className={classes.smallHeading}>Pay by Card</div>
            <div className={classes.cardContent}>
              <div className={classes.cardLeft}>
                Pay securely by debit/credit card. All payments are handled
                securely using Stripe.
              </div>
              <div className={classes.cardRight}>
                <div className={classes.cardRightImage}>
                  <div className={classes.stripeImage}>
                    <Icon name="Stripe" />
                  </div>
                  <div className={classes.paymentImage}>
                    <Icon name="Payments_Method" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <React.Suspense fallback={<div>Loading...</div>}>
          <PaymentForm
            onPurchase={onPurchase}
            onSuccess={onSuccess}
            onError={onError}
            requiresPayment={requiresPayment}
            hideOrderCard={hideOrderCard}
            registerSubmit={registerSubmit}
          />
        </React.Suspense>
      </div>
    </div>
  );
}
